import type { AdminRiskSignals } from "@/types/admin-fraud";
import type { MockUserId } from "@/lib/api/mock-data/users.mock";

// Signal `kind`s here are the gateway risk engine's real reason codes and the
// `reason` strings mirror its "<decision> · <path>" detail, so mock mode
// exercises the same vocabulary real mode receives. They used to be invented
// names, which is why a green mock suite hid a broken real-mode page.

const NOW = "2026-05-21T00:00:00Z";

/** Entries may omit the cluster/charge-off enrichment; defaults are injected below. */
type RawMockRiskSignals = Omit<
  AdminRiskSignals,
  "identityCluster" | "priorChargeOffs"
> &
  Partial<Pick<AdminRiskSignals, "identityCluster" | "priorChargeOffs">>;

const RAW_MOCK_RISK_SIGNALS: Record<MockUserId, RawMockRiskSignals> = {
  u_001_alice_active: {
    userId: "u_001_alice_active",
    riskLevel: "low",
    trialBlocked: false,
    householdExceptionGranted: false,
    householdMatchCount: 0,
    signals: [],
    computedAt: NOW,
  },
  u_002_bob_trial_pending: {
    userId: "u_002_bob_trial_pending",
    riskLevel: "low",
    trialBlocked: false,
    householdExceptionGranted: false,
    householdMatchCount: 0,
    signals: [],
    computedAt: NOW,
  },
  u_003_cara_trial_ending: {
    userId: "u_003_cara_trial_ending",
    riskLevel: "low",
    trialBlocked: false,
    householdExceptionGranted: false,
    householdMatchCount: 0,
    signals: [],
    computedAt: NOW,
  },
  u_004_dave_card_failed: {
    userId: "u_004_dave_card_failed",
    riskLevel: "medium",
    trialBlocked: false,
    householdExceptionGranted: false,
    householdMatchCount: 0,
    signals: [
      {
        kind: "device_velocity",
        level: "medium",
        detectedAt: "2026-05-13T11:15:00Z",
        reason: "route_to_prepaid · trial",
      },
    ],
    computedAt: NOW,
  },
  u_005_erin_charged_off: {
    userId: "u_005_erin_charged_off",
    riskLevel: "high",
    trialBlocked: true,
    householdExceptionGranted: false,
    householdMatchCount: 0,
    signals: [
      {
        kind: "user_trial_reuse",
        level: "high",
        detectedAt: "2026-04-02T13:20:00Z",
        reason: "route_to_prepaid · trial",
      },
    ],
    identityCluster: { collision: true, matchedAccountCount: 2, chargeOffCount: 1 },
    priorChargeOffs: [
      { occurredAt: "2026-04-02T13:20:00Z", serials: ["ZN-A78-11", "ZN-A78-12"] },
    ],
    computedAt: NOW,
  },
  u_006_finn_past_due: {
    userId: "u_006_finn_past_due",
    riskLevel: "medium",
    trialBlocked: false,
    householdExceptionGranted: false,
    householdMatchCount: 0,
    signals: [
      {
        kind: "device_velocity",
        level: "medium",
        detectedAt: "2026-05-01T08:00:00Z",
        reason: "route_to_prepaid · trial",
      },
      {
        kind: "avs_failed",
        level: "medium",
        detectedAt: "2026-05-01T08:00:00Z",
        reason: "route_to_prepaid · trial",
      },
    ],
    computedAt: NOW,
  },
  u_007_gina_suspended: {
    userId: "u_007_gina_suspended",
    riskLevel: "critical",
    trialBlocked: true,
    householdExceptionGranted: false,
    householdMatchCount: 0,
    signals: [
      {
        kind: "household_reuse",
        level: "high",
        detectedAt: "2026-03-12T09:00:00Z",
        reason: "route_to_prepaid · trial",
      },
      {
        kind: "radar_highest",
        level: "critical",
        detectedAt: "2026-03-12T09:01:00Z",
        reason: "block · trial",
      },
      {
        kind: "prepaid_card",
        level: "high",
        detectedAt: "2026-03-12T09:02:00Z",
        reason: "route_to_prepaid · trial",
      },
      {
        kind: "identity_cluster_collision",
        level: "high",
        detectedAt: "2026-03-12T09:03:00Z",
        reason: "route_to_prepaid · trial",
      },
    ],
    identityCluster: { collision: true, matchedAccountCount: 3, chargeOffCount: 2 },
    computedAt: NOW,
  },
  u_008_hugo_cancelled: {
    userId: "u_008_hugo_cancelled",
    riskLevel: "low",
    trialBlocked: false,
    householdExceptionGranted: false,
    householdMatchCount: 0,
    signals: [],
    computedAt: NOW,
  },
};

const EMPTY_CLUSTER: AdminRiskSignals["identityCluster"] = {
  collision: false,
  matchedAccountCount: 0,
  chargeOffCount: 0,
};

/** Inject cluster/charge-off defaults so every mock matches the backend shape. */
export const MOCK_RISK_SIGNALS: Record<MockUserId, AdminRiskSignals> =
  Object.fromEntries(
    Object.entries(RAW_MOCK_RISK_SIGNALS).map(([id, s]) => [
      id,
      {
        ...s,
        identityCluster: s.identityCluster ?? EMPTY_CLUSTER,
        priorChargeOffs: s.priorChargeOffs ?? [],
      },
    ]),
  ) as Record<MockUserId, AdminRiskSignals>;
